import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { COMPANY_INFO } from '@/lib/brandConfig';
import { CALIFORNIA_SHOWROOM } from '@/lib/california/californiaConfig';

export type LocationRegion = 'texas' | 'texas-houston' | 'texas-austin' | 'texas-san-antonio' | 'california' | 'national';

export interface LocationContext {
  region: LocationRegion;
  businessName: string;
  showroomName: string;
  address: {
    street: string;
    city: string;
    state: string;
    stateAbbr: string;
    zip: string;
    full: string;
  };
  phone: string;
  email: string;
  hours: {
    weekday: string;
    weekend: string;
  };
  showroomLink: string;
  scheduleLink: string;
  isShowroomLocation: boolean;
}

const TEXAS_LOCATION: LocationContext = {
  region: 'texas',
  businessName: 'BBQ Island Liquidators – Texas',
  showroomName: 'Dallas Showroom',
  address: {
    street: COMPANY_INFO.address.street,
    city: COMPANY_INFO.address.city,
    state: COMPANY_INFO.address.state,
    stateAbbr: 'TX',
    zip: COMPANY_INFO.address.zip,
    full: COMPANY_INFO.address.full,
  },
  phone: COMPANY_INFO.phone,
  email: COMPANY_INFO.email,
  hours: COMPANY_INFO.hours,
  showroomLink: '/showroom',
  scheduleLink: '/schedule',
  isShowroomLocation: true,
};

const CALIFORNIA_LOCATION: LocationContext = {
  region: 'california',
  businessName: 'BBQ Island Liquidators – California',
  showroomName: 'Inland Empire Showroom',
  address: {
    street: CALIFORNIA_SHOWROOM.address.street,
    city: CALIFORNIA_SHOWROOM.address.city,
    state: CALIFORNIA_SHOWROOM.address.state,
    stateAbbr: CALIFORNIA_SHOWROOM.address.stateAbbr,
    zip: CALIFORNIA_SHOWROOM.address.zip,
    full: CALIFORNIA_SHOWROOM.address.full,
  },
  phone: CALIFORNIA_SHOWROOM.phone,
  email: 'hello@bbqislandliquidators.com', // Consistent with TX
  hours: CALIFORNIA_SHOWROOM.hours,
  showroomLink: '/showrooms/inland-empire/',
  scheduleLink: '/ca/schedule',
  isShowroomLocation: true,
};

const NATIONAL_LOCATION: LocationContext = {
  region: 'national',
  businessName: 'BBQ Island Liquidators',
  showroomName: 'Our Showrooms',
  address: {
    street: '',
    city: '',
    state: '',
    stateAbbr: '',
    zip: '',
    full: '',
  },
  phone: COMPANY_INFO.phone, // Default to Texas
  email: COMPANY_INFO.email,
  hours: COMPANY_INFO.hours,
  showroomLink: '/locations',
  scheduleLink: '/schedule',
  isShowroomLocation: false,
};

/**
 * Determines the location context based on the current URL path.
 * This is used for footer rendering, NAP consistency, and DNI routing.
 */
export const getLocationContextFromPath = (pathname: string): LocationContext => {
  const normalizedPath = pathname.toLowerCase();
  
  // California pages
  if (
    normalizedPath.startsWith('/ca/') ||
    normalizedPath.startsWith('/ppc/ca/') ||
    normalizedPath.startsWith('/showrooms/inland-empire')
  ) {
    return CALIFORNIA_LOCATION;
  }
  
  // Houston-specific pages (distinct from DFW)
  if (
    normalizedPath === '/tx/houston/' ||
    normalizedPath === '/tx/houston' ||
    normalizedPath === '/ppc/tx/houston/' ||
    normalizedPath === '/ppc/tx/houston'
  ) {
    return {
      ...TEXAS_LOCATION,
      region: 'texas-houston' as LocationRegion,
      showroomName: 'Houston Service Area',
      isShowroomLocation: false, // Houston is delivery only, no showroom
    };
  }
  
  // Austin-specific pages (distinct from DFW)
  if (
    normalizedPath === '/tx/austin/' ||
    normalizedPath === '/tx/austin' ||
    normalizedPath === '/ppc/tx/austin/' ||
    normalizedPath === '/ppc/tx/austin'
  ) {
    return {
      ...TEXAS_LOCATION,
      region: 'texas-austin' as LocationRegion,
      showroomName: 'Austin Service Area',
      isShowroomLocation: false, // Austin is delivery only, no showroom
    };
  }
  
  // San Antonio-specific pages (distinct from DFW)
  if (
    normalizedPath === '/tx/san-antonio/' ||
    normalizedPath === '/tx/san-antonio' ||
    normalizedPath === '/ppc/tx/san-antonio/' ||
    normalizedPath === '/ppc/tx/san-antonio'
  ) {
    return {
      ...TEXAS_LOCATION,
      region: 'texas-san-antonio' as LocationRegion,
      showroomName: 'San Antonio Service Area',
      isShowroomLocation: false, // San Antonio is delivery only, no showroom
    };
  }
  
  // Texas pages (DFW is default)
  if (
    normalizedPath.startsWith('/tx/') ||
    normalizedPath.startsWith('/ppc/tx/') ||
    normalizedPath.startsWith('/showroom') && !normalizedPath.includes('inland-empire')
  ) {
    return TEXAS_LOCATION;
  }
  
  // Homepage and other neutral pages
  if (normalizedPath === '/' || normalizedPath === '') {
    return NATIONAL_LOCATION;
  }
  
  // Default to Texas for most pages (legacy behavior)
  return TEXAS_LOCATION;
};

/**
 * React hook to get the current location context based on the URL.
 */
export const useLocationContext = (): LocationContext => {
  const location = useLocation();
  
  return useMemo(() => {
    return getLocationContextFromPath(location.pathname);
  }, [location.pathname]);
};

/**
 * Get footer links based on the location region.
 */
export const getLocationFooterLinks = (region: LocationRegion) => {
  const texasLinks = [
    { label: 'Dallas–Fort Worth', href: '/tx/dallas/' },
    { label: 'Houston', href: '/tx/houston/' },
    { label: 'Austin', href: '/tx/austin/' },
    { label: 'San Antonio', href: '/tx/san-antonio/' },
  ];
  
  const californiaLinks = [
    { label: 'Inland Empire', href: '/ca/inland-empire/' },
    { label: 'Los Angeles', href: '/ca/los-angeles/' },
    { label: 'Visit Showroom', href: '/showrooms/inland-empire/' },
  ];
  
  if (region === 'national') {
    return {
      showrooms: [
        { label: 'Dallas Showroom (TX)', href: '/showroom' },
        { label: 'Inland Empire Showroom (CA)', href: '/showrooms/inland-empire/' },
      ],
      texas: texasLinks,
      california: californiaLinks,
    };
  }
  
  // California pages get California links
  if (region === 'california') {
    return { locations: californiaLinks };
  }
  
  // All Texas regions (texas, texas-houston, texas-austin, texas-san-antonio) get Texas links
  return { locations: texasLinks };
};

/**
 * Get footer description based on region - ensures geographic accuracy
 */
export const getFooterDescription = (region: LocationRegion): string => {
  switch (region) {
    case 'california':
      return "Southern California's premier destination for custom outdoor kitchens, premium grills, and outdoor cooking equipment. Factory-direct pricing with delivery throughout California.";
    case 'texas-houston':
    case 'texas-austin':
    case 'texas-san-antonio':
      return "Texas's premier destination for custom outdoor kitchens, premium grills, smokers, and outdoor cooking equipment. Factory-direct pricing with delivery throughout Texas.";
    case 'texas':
    default:
      return "Dallas–Fort Worth's premier destination for custom outdoor kitchens, premium grills, smokers, and outdoor cooking equipment. Factory-direct pricing with white-glove delivery throughout Texas.";
  }
};
